import React, { useState, Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faShieldHalved, faLock, faCheck } from "@fortawesome/free-solid-svg-icons";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const AddCardForm = ({ clientSecret, onSaveSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) return;
        setIsProcessing(true);
        setError(null);
        const cardElement = elements.getElement(CardElement);
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: cardElement,
            },
        });
        setIsProcessing(false);
        if (error) {
            if (error.message && error.message.includes("Invalid API Key")) {
                setError("The payment gateway is currently misconfigured. Please try again later or contact support.");
            } else {
                setError(error.message || "An unexpected error occurred. Please try again.");
            }
        } else {
            onSaveSuccess();
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-100 p-2">
            <div className="d-flex align-items-center mb-4 p-3 rounded-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div className="bg-primary bg-opacity-10 text-primary rounded d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                    <FontAwesomeIcon icon={faShieldHalved} className="fs-5" />
                </div>
                <div>
                    <h6 className="fw-bold mb-1 fs-9 text-body-emphasis">Bank-Level Security</h6>
                    <p className="text-body-tertiary fs-10 mb-0" style={{ lineHeight: '1.4' }}>
                        Your details are protected with 256-bit encryption. We do not store your raw card information.
                    </p>
                </div>
            </div>

            <div className="mb-4">
                <label className="form-label fw-bold fs-9 text-body-emphasis mb-2 d-flex justify-content-between align-items-center">
                    <span>Card Information <span className="text-danger">*</span></span>
                    <span className="text-success fs-10 bg-success bg-opacity-10 px-2 py-1 rounded-pill d-flex align-items-center fw-semibold"><FontAwesomeIcon icon={faLock} className="me-1" />Encrypted</span>
                </label>
                <div className="border rounded-3 p-3 bg-white shadow-sm" style={{ borderColor: '#d5dbdb' }}>
                    <CardElement options={{
                        style: {
                            base: {
                                fontSize: '15px',
                                fontFamily: '"Inter", sans-serif',
                                color: '#16191f',
                                '::placeholder': {
                                    color: '#aab7b8',
                                },
                                iconColor: '#0972d3',
                            },
                            invalid: {
                                color: '#d13212',
                                iconColor: '#d13212',
                            },
                        },
                        hidePostalCode: true,
                    }} />
                </div>
            </div>
            {error && (
                <div className="alert alert-danger d-flex align-items-start mt-3 mb-4 py-3 px-3 border-0 bg-danger bg-opacity-10 text-danger rounded-3" role="alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="fs-5 mt-1 me-3" />
                    <div>
                        <div className="fw-bold fs-9 mb-1">Could not save card</div>
                        <div className="fs-9 fw-medium opacity-75">{error}</div>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: '1px solid #eaeded' }}>
                <button type="button" className="auth-premium-secondary-btn px-4" style={{ width: 'auto', padding: '10px 16px', fontSize: '0.85rem' }} onClick={onCancel} disabled={isProcessing}>
                    Cancel
                </button>
                <button type="submit" className="auth-premium-btn px-4" style={{ width: 'auto', padding: '10px 16px', fontSize: '0.85rem' }} disabled={!stripe || isProcessing}>
                    {isProcessing ? (
                        <Fragment><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...</Fragment>
                    ) : (
                        <Fragment><FontAwesomeIcon icon={faCheck} className="me-2" />Save Securely</Fragment>
                    )}
                </button>
            </div>
        </form>
    );
};

export default AddCardForm;
